import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { FormTemplateResponse } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { EditableSectionName } from './EditableSectionName';
import { EditFormTemplateHeader } from './EditFieldModal/EditFormTemplateHeader';
import { EditFormTemplateSectionFields } from './EditFormTemplateSectionFields';
import { useEditableFormTemplateState } from './hooks/useEditableFormTemplateState';
import { ReorderableFormTemplateGroupsList } from './ReorderableFormTemplateGroupsList';
import { ColumnWrapper, MainContent, Sidebar, StyledMain } from './styled';

export type FormTemplateEditorProps = {
  titleVerb: string;
  beginningFormTemplate?: FormTemplateResponse;
  closeUrl: string;
  handleSaveFormTemplate: (formTemplate: FormTemplateResponse) => Promise<void>;
  isSubmitting: boolean;
  sidebarComponent: React.ReactNode;
  sidebarWidth?: string;
  isDirty: boolean;
  setIsDirty: (isDirty: boolean) => void;
};

export function FormTemplateEditor({
  titleVerb,
  beginningFormTemplate,
  closeUrl,
  handleSaveFormTemplate,
  isSubmitting,
  sidebarComponent,
  sidebarWidth,
  isDirty,
  setIsDirty,
}: FormTemplateEditorProps) {
  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();

  const {
    activeGroupId,
    formTemplate,
    setActiveGroupId,
    updateField,
    updateFieldOrder,
    updateGroupName,
    updateGroupOrder,
  } = useEditableFormTemplateState({
    passedInFormTemplate: beginningFormTemplate,
    setIsDirty,
  });

  const activeGroupFields = useMemo(
    () =>
      activeGroupId
        ? formTemplate?.groups.find((group) => group.id === activeGroupId)
            ?.fields
        : formTemplate?.groups[0]?.fields,
    [activeGroupId, formTemplate],
  );

  const handleSubmit = useCallback(async () => {
    if (!formTemplate) return;
    await handleSaveFormTemplate(formTemplate);
  }, [formTemplate, handleSaveFormTemplate]);

  return (
    <StyledMain>
      <EditFormTemplateHeader
        title={`${titleVerb} ${formTemplate?.name || 'form template'}`}
        closeUrl={closeUrl}
        formIsDirty={isDirty}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
      />
      <ColumnWrapper>
        <Sidebar $sidebarWidth={sidebarWidth}>
          <SkipLink linkText="Skip to form fields" />
          {sidebarComponent}
          <ReorderableFormTemplateGroupsList
            orderedGroups={formTemplate?.groups || []}
            updateGroupOrder={updateGroupOrder}
            setActiveGroupId={setActiveGroupId}
            activeGroupId={activeGroupId}
          />
        </Sidebar>
        <MainContent>
          <SkipLinkTarget />
          {activeGroupId && formTemplate && (
            <>
              <EditableSectionName
                updateGroupName={updateGroupName}
                activeGroupId={activeGroupId}
                formTemplate={formTemplate}
              />
              <EditFormTemplateSectionFields
                orderedFields={activeGroupFields || []}
                updateFieldOrder={updateFieldOrder}
                updateField={updateField}
              />
            </>
          )}
        </MainContent>
      </ColumnWrapper>
    </StyledMain>
  );
}
